import { useContext, useEffect, useState } from 'react';
import { Divider } from 'primereact/divider';
import { useWindowSize } from 'react-use';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { AlertVariant, IMAGE_TYPE, MOBILE_BREAKPOINT, OrgPermissionKeys, OrgSubPermissionKeys } from '../../../../constants/constants';
import { _S3ImageResponse, _ThemeColorsType, _ToastMessageProps, _VenuePickerOption } from '../../../../constants/staticTypes';
import withSidebar from '../../../../hoc/withSideBar/withSideBar';
import logger from '../../../../utils/logger';
import { getVenueOptions } from '../../../../helpers/venueHelper';
import {
    BRANDING,
    CANCEL,
    CUSTOMIZED,
    DEFAULT,
    LOGO,
    PREVIEW,
    PRIMARY_COLOR,
    SECONDARY_COLOR,
    SETTINGS,
    TERTIARY_COLOR,
    TEXT_COLOR,
    THEME_MANAGEMENT,
    UPDATE,
    UPLOAD_MOBILE_LOGO,
    UPLOAD_WEB_LOGO,
    UPLOAD_DIMENSION_MSG,
    APPLY_TO,
} from '../../../../constants/strings';
import { useTranslation } from '../../../../hooks/useTranslation';
import { VENUE_SETTINGS } from '../../../../constants/queryKeys';
import MlxDropDown from '../../../../components/common/MlxDropDown/MlxDropDown';
import MlxMultiSelect from '../../../../components/common/MlxMultiSelect/MlxMultiSelect';

import MlxBlockUI from '../../../../components/common/MlxBlockUI/MlxBlockUI';
import MlxButton from '../../../../components/common/MlxButton/MlxButton';
import MlxImageUpload from '../../../../components/common/MlxImageUpload/MlxImageUpload';
import MlxToolTip from '../../../../components/common/MlxToolTip/MlxToolTip';
import ThemeSelection from './components/ThemeSelection';
import {
    _UpdateSettingsData,
    getVenueSettings,
    updateVenueSettings,
    applyBrandingToVenues,
    _ApplyToVenuesData,
} from '../../../../helpers/venueSettingsHelper';
import { defaultToastData } from '../../../../helpers/commonHelper';
import MlxToast from '../../../../components/common/MlxToast/MlxToast';
import HttpServiceHelper from '../../../../helpers/httpServiceHelper';
import { checkActionSubPermission } from '../../../../utils/common';
import { AuthCtx } from '../../../../context/AuthCtxProvider';
import './VenueSettingsManagement.scss';

const searchColumns = ['firstName', 'lastName', 'email'];

const languageTexts = {
    SETTINGS: SETTINGS,
    BRANDING: BRANDING,
    LOGO: LOGO,
    UPLOAD_WEB_LOGO: UPLOAD_WEB_LOGO,
    UPLOAD_MOBILE_LOGO: UPLOAD_MOBILE_LOGO,
    CANCEL: CANCEL,
    UPDATE: UPDATE,
    THEME_MANAGEMENT: THEME_MANAGEMENT,
    DEFAULT: DEFAULT,
    CUSTOMIZED: CUSTOMIZED,
    PRIMARY_COLOR: PRIMARY_COLOR,
    SECONDARY_COLOR: SECONDARY_COLOR,
    TERTIARY_COLOR: TERTIARY_COLOR,
    TEXT_COLOR: TEXT_COLOR,
    PREVIEW: PREVIEW,
    UPLOAD_DIMENSION_MSG: UPLOAD_DIMENSION_MSG,
    APPLY_TO: APPLY_TO,
};

function VenueSettingsManagement() {
    const { width: screenWidth } = useWindowSize();
    const queryClient = useQueryClient();
    const translatedText = useTranslation(languageTexts, 'none');
    const updateVenueSettingsMutation = useMutation(HttpServiceHelper);
    const applyBrandingMutation = useMutation(HttpServiceHelper);
    const { userType } = useContext(AuthCtx);

    const [toastMessage, setToastMessage] = useState<_ToastMessageProps>(defaultToastData);
    const [searchBarVisible, setSearchBarVisible] = useState(screenWidth > MOBILE_BREAKPOINT);
    const [isVenueListLoading, setIsVenueListLoading] = useState<boolean>(false);
    const [venueOptions, setVenueOptions] = useState<_VenuePickerOption[]>([]);
    const [selectedVenueOption, setSelectedVenueOption] = useState<string>();
    const [selectedVenuesToApply, setSelectedVenuesToApply] = useState<string[]>([]);
    const [isDefaultTheme, setIsDefaultTheme] = useState<boolean>(false);
    const [defaultColors, setDefaultColors] = useState<_ThemeColorsType>({
        primary: '',
        secondary: '',
    });
    const [customColors, setCustomColors] = useState<_ThemeColorsType>({});

    const [webLogo, setWebLogo] = useState<_S3ImageResponse>({
        imageKey: null,
        imageUrl: null, // need to get the image url using key
    });
    const [mobileLogo, setMobileLogo] = useState<_S3ImageResponse>({
        imageKey: null,
        imageUrl: null, // need to get the image url using key
    });

    useEffect(() => {
        setIsVenueListLoading(true);
        const getVenues = async () => {
            const venueOptionsList = await getVenueOptions(queryClient);
            setVenueOptions(venueOptionsList);
            venueOptionsList?.length > 0 && setSelectedVenueOption(venueOptionsList[0].id);
            setIsVenueListLoading(false);
        };
        getVenues();
    }, []);

    // branding can be applied to other active venues only
    const applyToVenueOptions = venueOptions.filter((venue) => venue.id !== selectedVenueOption && venue.isActive);
    const hasInactiveVenues = venueOptions.some((venue) => !venue.isActive);

    const venueSettingsQuery = useQuery({
        queryKey: VENUE_SETTINGS,
        queryFn: () => getVenueSettings(queryClient, selectedVenueOption),
        onSuccess: (response) => {
            const responseData = response?.data?.data;
            setWebLogo({
                imageKey: responseData?.webLogoPhotoKey !== '' ? responseData?.webLogoPhotoKey : responseData?.defaultBranding.webLogoKey,
                imageUrl: responseData?.webLogo !== '' ? responseData?.webLogo : responseData?.defaultBranding.webLogo,
            });
            setMobileLogo({
                imageKey:
                    responseData?.mobileLogoPhotoKey !== ''
                        ? responseData?.mobileLogoPhotoKey
                        : responseData?.defaultBranding.mobileLogoKey,
                imageUrl: responseData?.mobileLogo !== '' ? responseData?.mobileLogo : responseData?.defaultBranding.mobileLogo,
            });
            setDefaultColors({
                primary: responseData?.defaultBranding.primaryColor,
                secondary: responseData?.defaultBranding.secondaryColor,
            });
            if (responseData?.primaryColor !== '' && responseData?.secondaryColor !== '') {
                setCustomColors({
                    primary: responseData?.primaryColor,
                    secondary: responseData?.secondaryColor,
                });
                setIsDefaultTheme(false);
            } else {
                setCustomColors({
                    primary: '#00b551',
                    secondary: '#262930',
                });
                setIsDefaultTheme(true);
            }
        },
        onError: (error) => logger.log('Failed to fetch admin staff list', error),
        refetchOnWindowFocus: false,
        enabled: false,
    });

    useEffect(() => {
        if (!selectedVenueOption) return;
        venueSettingsQuery.refetch();
    }, [selectedVenueOption]);

    const onSuccess = (data: any) => {
        setToastMessage({
            message: data?.message ?? '',
            variant: AlertVariant.SUCCESS,
            show: true,
        });
        venueSettingsQuery.refetch();
    };

    const onSuccessWithApply = (data: any) => {
        setToastMessage({
            message: data?.message ?? '',
            variant: AlertVariant.SUCCESS,
            show: true,
        });
        venueSettingsQuery.refetch();

        // After successful venue settings update, make the apply branding API call if needed
        if (selectedVenuesToApply.length > 0) {
            // applyToAllVenues would also reach inactive venues, so send explicit ids whenever any are inactive
            const isApplyToAll = !hasInactiveVenues && selectedVenuesToApply.length === applyToVenueOptions.length;
            const applyToData: _ApplyToVenuesData = {
                id: selectedVenueOption!,
                applyToAllVenues: isApplyToAll,
                ...(isApplyToAll ? {} : { venueIds: selectedVenuesToApply }),
            };

            applyBrandingToVenues(applyBrandingMutation, applyToData, onSuccess, onError);
        }
    };

    const onError = (error: string, variant: string) => {
        setToastMessage({
            message: error,
            variant,
            show: true,
        });
    };

    const handleSettingsUpdate = () => {
        // Update the current venue settings
        let data: _UpdateSettingsData = {
            id: selectedVenueOption,
            webLogo: webLogo.imageKey,
            mobileLogo: mobileLogo.imageKey,
            primaryColor: isDefaultTheme ? '' : (customColors.primary ?? ''),
            secondaryColor: isDefaultTheme ? '' : (customColors.secondary ?? ''),
        };

        // Use different success handler based on whether we need to apply to other venues
        const successHandler = selectedVenuesToApply.length > 0 ? onSuccessWithApply : onSuccess;
        updateVenueSettings(updateVenueSettingsMutation, data, successHandler, onError);
    };

    return (
        <MlxBlockUI
            isLoading={
                isVenueListLoading ||
                venueSettingsQuery.isLoading ||
                venueSettingsQuery.isFetching ||
                updateVenueSettingsMutation?.isLoading ||
                applyBrandingMutation?.isLoading
            }
            opacity={0.5}
        >
            <MlxToast
                data-testid="toast"
                message={toastMessage.message}
                onClose={() => setToastMessage(defaultToastData)}
                show={toastMessage.show}
                variant={toastMessage.variant}
            />
            <div className="page-section h-100">
                <span className="page-header d-flex justify-content-between">
                    <span>{translatedText.SETTINGS}</span>
                    <MlxDropDown
                        dropDownSize="large"
                        options={venueOptions}
                        optionLabel="venueName"
                        optionValue="id"
                        value={selectedVenueOption}
                        placeholder={translatedText.SELECT_VENUE}
                        onChange={(e) => {
                            setSelectedVenueOption(e.value);
                        }}
                        style={{ width: '200px' }}
                    />
                </span>
                <div className="page-content overflow-auto m-0">
                    <div className="content-header flex-grow-0">
                        <span className="header-title">{searchBarVisible ? translatedText.BRANDING : ''}</span>
                        <div className="header-fields"></div>
                    </div>
                    <Divider className="content-divider" />
                    <div className="content-body flex-grow-1 overflow-auto" style={{ padding: '1rem' }}>
                        <div className="settings-logo-section">
                            <div className="settings-header">{translatedText.LOGO}</div>
                            <div className="step4-container-form custom-logo-upload">
                                <div className="d-flex flex-column me-3">
                                    <div className="d-flex align-items-center">
                                        <span className="mb-1">{translatedText.UPLOAD_WEB_LOGO}</span>
                                        <MlxToolTip target={'.uploadWebLogoInfo'}>
                                            <i
                                                className={`pi pi-info-circle mb-1 ms-2 text-primary uploadWebLogoInfo`}
                                                data-pr-tooltip={`${translatedText.UPLOAD_DIMENSION_MSG}: ${IMAGE_TYPE['WEB_LOGO'].width}x${IMAGE_TYPE['WEB_LOGO'].height}`}
                                                data-pr-position="left"
                                            ></i>
                                        </MlxToolTip>
                                    </div>
                                    <MlxImageUpload
                                        mode="basic"
                                        image={webLogo}
                                        setImage={setWebLogo}
                                        validateImageDimension={IMAGE_TYPE['WEB_LOGO']}
                                        cropImage={true}
                                    />
                                </div>
                                <div className="d-flex flex-column">
                                    <div className="d-flex align-items-center">
                                        <span className="mb-1">{translatedText.UPLOAD_MOBILE_LOGO}</span>
                                        <MlxToolTip target={'.uploadMobileLogoInfo'}>
                                            <i
                                                className={`pi pi-info-circle mb-1 ms-2 text-primary uploadMobileLogoInfo`}
                                                data-pr-tooltip={`${translatedText.UPLOAD_DIMENSION_MSG}: ${IMAGE_TYPE['MOBILE_LOGO'].width}x${IMAGE_TYPE['MOBILE_LOGO'].height}`}
                                                data-pr-position="left"
                                            ></i>
                                        </MlxToolTip>
                                    </div>
                                    <MlxImageUpload
                                        mode="basic"
                                        image={mobileLogo}
                                        setImage={setMobileLogo}
                                        validateImageDimension={IMAGE_TYPE['MOBILE_LOGO']}
                                        cropImage={true}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="settings-theme-section">
                            <div className="settings-header mb-4">{translatedText.THEME_MANAGEMENT}</div>
                            <ThemeSelection
                                isDefaultTheme={isDefaultTheme}
                                setIsDefaultTheme={setIsDefaultTheme}
                                customColors={customColors}
                                setCustomColors={setCustomColors}
                                defaultColors={defaultColors}
                            />

                            {/* Apply to box */}
                            <div className="apply-to-box">
                                <div className={`d-flex gap-3 ${screenWidth < 400 ? 'flex-column' : 'align-items-center'}`}>
                                    <span className="fw-medium" style={{ minWidth: '70px', flexShrink: 0 }}>
                                        {translatedText.APPLY_TO}
                                    </span>
                                    <MlxMultiSelect
                                        value={selectedVenuesToApply}
                                        options={applyToVenueOptions}
                                        optionLabel="venueName"
                                        optionValue="id"
                                        placeholder="Select Venue"
                                        display="chip"
                                        onChange={(e) => setSelectedVenuesToApply(e.value)}
                                        style={{
                                            flex: 1,
                                            minWidth: '250px',
                                            maxWidth: '300px',
                                        }}
                                        filter
                                        showClear
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {(userType === 'organizationAdmin' ||
                    checkActionSubPermission(
                        queryClient,
                        OrgPermissionKeys.MANAGE_VENUE_SETTINGS,
                        OrgSubPermissionKeys.EDIT_VENUE_SETTINGS
                    )) && (
                    <div className="settings-footer">
                        {/* <MlxButton
                        label={translatedText.CANCEL}
                        onClick={(event) => {
                            // event.preventDefault();
                            // setResetKey((prev) => prev + 1);
                        }}
                        icon="ri-close-large-line"
                        iconPos="right"
                        size="small"
                        outlined
                    /> */}
                        <MlxButton
                            className="ms-2"
                            label={translatedText.UPDATE}
                            onClick={(event) => {
                                event.preventDefault();
                                handleSettingsUpdate();
                            }}
                            icon="ri-check-line"
                            iconPos="right"
                            size="small"
                            disabled={updateVenueSettingsMutation?.isLoading || applyBrandingMutation?.isLoading}
                            loading={updateVenueSettingsMutation?.isLoading || applyBrandingMutation?.isLoading}
                        />
                    </div>
                )}
            </div>
        </MlxBlockUI>
    );
}

export default withSidebar(VenueSettingsManagement);
